import { useState, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import { C } from "../theme";
import { uploadProfilePic, deleteProfilePic, fetchProfilePicUrl } from "../lib/db";

const STORAGE_PREFIX = "taxshop_profile_pic_";

export function getProfilePic(name) {
  if (!name) return null;
  try { return localStorage.getItem(STORAGE_PREFIX + name) || null; }
  catch { return null; }
}

export function setProfilePic(name, dataUrl) {
  try {
    localStorage.setItem(STORAGE_PREFIX + name, dataUrl);
    window.dispatchEvent(new CustomEvent("taxshop-profile-pic-changed", { detail: { name } }));
  } catch {}
}

export function removeProfilePic(name) {
  try {
    localStorage.removeItem(STORAGE_PREFIX + name);
    window.dispatchEvent(new CustomEvent("taxshop-profile-pic-changed", { detail: { name } }));
  } catch {}
}

// ── Sync all named profile pics from Supabase into localStorage.
//    Call this once on app load (non-blocking). Handles current + future members.
export async function syncAllProfilePics(names) {
  if (!names?.length) return;
  await Promise.allSettled(names.map(async (name) => {
    if (!name) return;
    const cached = getProfilePic(name);
    // Skip if we already have a URL (not a base64 blob — those need re-upload)
    if (cached && cached.startsWith("http")) return;
    try {
      const url = await fetchProfilePicUrl(name);
      if (url) setProfilePic(name, url);
    } catch {}
  }));
}

// ── Avatar: shows photo or coloured initials fallback ──────────
// Falls back to Supabase Storage when localStorage is empty (cross-device sync).
export function Avatar({ name, initials, color, size = 56, radius = 16, fontSize = 20 }) {
  const [pic, setPic] = useState(() => getProfilePic(name));

  // If name changes, re-read localStorage immediately
  const localPic = getProfilePic(name);
  if (pic !== localPic && localPic) setPic(localPic);

  // If nothing in localStorage, try Supabase Storage once
  useEffect(() => {
    if (!name || getProfilePic(name)) return;
    let cancelled = false;
    fetchProfilePicUrl(name).then((url) => {
      if (cancelled || !url) return;
      setProfilePic(name, url);
      setPic(url);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [name]);

  // Listen for profile-pic-changed events (uploaded on another tab / component)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.name === name) {
        const updated = getProfilePic(name);
        setPic(updated);
      }
    };
    window.addEventListener("taxshop-profile-pic-changed", handler);
    return () => window.removeEventListener("taxshop-profile-pic-changed", handler);
  }, [name]);

  if (pic) {
    return (
      <img
        src={pic}
        alt={name}
        style={{
          width: size, height: size, borderRadius: radius,
          objectFit: "cover", flexShrink: 0,
          boxShadow: `0 4px 14px ${color || C.orange}55`,
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, flexShrink: 0,
      background: color || C.orange,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 800, fontSize, color: "#fff", letterSpacing: "0.05em",
      boxShadow: `0 4px 14px ${color || C.orange}55`,
    }}>
      {initials || (name || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

// ── ProfilePicEditor: upload / remove button shown below avatar ─
export function ProfilePicEditor({ name, initials, color, size = 56, radius = 16, onChanged }) {
  const [pic, setPic]         = useState(() => getProfilePic(name));
  const [hovered, setHovered] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef              = useRef();

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Please choose an image under 3 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      // Show immediately from localStorage
      setProfilePic(name, dataUrl);
      setPic(dataUrl);
      if (onChanged) onChanged(dataUrl);
      // Upload to Supabase Storage so it survives browser cache clears
      setUploading(true);
      try {
        const signedUrl = await uploadProfilePic(name, dataUrl);
        setProfilePic(name, signedUrl);
        setPic(signedUrl);
        if (onChanged) onChanged(signedUrl);
      } catch {
        // localStorage copy already saved — will work until cache is cleared
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemove = () => {
    removeProfilePic(name);
    setPic(null);
    setConfirm(false);
    if (onChanged) onChanged(null);
    deleteProfilePic(name).catch(() => {});
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      {/* Lightbox overlay */}
      {lightbox && pic && (
        <div
          onClick={() => setLightbox(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.82)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <img
            src={pic}
            alt={name}
            style={{
              maxWidth: "85vw", maxHeight: "85vh",
              borderRadius: 18, objectFit: "contain",
              boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
            }}
            onClick={e => e.stopPropagation()}
          />
          <button
            onClick={() => setLightbox(false)}
            style={{
              position: "absolute", top: 20, right: 24,
              background: "rgba(255,255,255,0.15)", border: "none",
              color: "#fff", fontSize: 22, cursor: "pointer",
              width: 40, height: 40, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >✕</button>
        </div>
      )}

      {/* Avatar with hover overlay */}
      <div
        style={{ position: "relative", flexShrink: 0 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {pic ? (
          <img
            src={pic} alt={name}
            onClick={() => setLightbox(true)}
            style={{
              width: size, height: size, borderRadius: radius, objectFit: "cover",
              boxShadow: `0 4px 14px ${color || C.orange}55`,
              transition: "opacity 0.2s", opacity: hovered ? 0.85 : 1,
              cursor: "zoom-in",
            }}
          />
        ) : (
          <div
            onClick={() => inputRef.current?.click()}
            style={{
              width: size, height: size, borderRadius: radius, cursor: "pointer",
              background: hovered ? `${color || C.orange}CC` : (color || C.orange),
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 800, fontSize: size * 0.35, color: "#fff",
              boxShadow: `0 4px 14px ${color || C.orange}55`,
              transition: "all 0.2s",
            }}
            title="Click to upload photo"
          >
            {hovered ? "📷" : (initials || (name || "?").slice(0, 2).toUpperCase())}
          </div>
        )}

        {/* Zoom hint on photo hover */}
        {pic && hovered && !uploading && (
          <div style={{
            position: "absolute", inset: 0, borderRadius: radius,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: size * 0.28, pointerEvents: "none",
          }}>🔍</div>
        )}
        {uploading && (
          <div style={{
            position: "absolute", inset: 0, borderRadius: radius,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, color: "#fff", fontWeight: 700, pointerEvents: "none",
          }}>Saving…</div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 6 }}>
        <button
          onClick={() => !uploading && inputRef.current?.click()}
          style={{
            padding: "4px 12px", borderRadius: 20, border: `1.5px solid ${color || C.orange}`,
            background: "#fff", color: color || C.orange, fontSize: 11, fontWeight: 700,
            cursor: uploading ? "not-allowed" : "pointer", fontFamily: "inherit",
            opacity: uploading ? 0.6 : 1,
          }}
        >
          {uploading ? "Saving…" : pic ? "Change Photo" : "Upload Photo"}
        </button>

        {pic && !confirm && (
          <button
            onClick={() => setConfirm(true)}
            style={{
              padding: "4px 10px", borderRadius: 20, border: "1.5px solid #E2E8F0",
              background: "#fff", color: C.textSub, fontSize: 11, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            Remove
          </button>
        )}

        {confirm && (
          <>
            <button
              onClick={handleRemove}
              style={{
                padding: "4px 10px", borderRadius: 20, border: "1.5px solid #EF4444",
                background: "#FFF1F2", color: "#EF4444", fontSize: 11, fontWeight: 700,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Yes, remove
            </button>
            <button
              onClick={() => setConfirm(false)}
              style={{
                padding: "4px 10px", borderRadius: 20, border: "1.5px solid #E2E8F0",
                background: "#fff", color: C.textSub, fontSize: 11, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
          </>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFile}
      />
    </div>
  );
}
